import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Search,
  Package,
  FileSpreadsheet,
  Coffee,
  Sparkles,
  Car,
  Wrench,
  DollarSign,
} from "lucide-react";

const CLASSIFICATIONS = [
  { value: "PAP", label: "Papelería y Artículos de Oficina", icon: FileSpreadsheet, color: "bg-blue-500" },
  { value: "AYB", label: "Alimentos y Bebidas", icon: Coffee, color: "bg-orange-500" },
  { value: "LIM", label: "Limpieza", icon: Sparkles, color: "bg-green-500" },
  { value: "INV", label: "Insumos Vehículos", icon: Car, color: "bg-purple-500" },
  { value: "MAN", label: "Mantenimiento", icon: Wrench, color: "bg-slate-500" },
];

const SettingsArticulos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClassification, setFilterClassification] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: "", categoria: "LIM" });

  useEffect(() => {
    fetchArticulos();
  }, []);

  const fetchArticulos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/articulos-catalogo/costos`, { withCredentials: true });
      setArticulos(res.data);
    } catch (error) {
      toast.error("Error al cargar artículos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/articulos-catalogo/${editingItem.id}`, formData, { withCredentials: true });
        toast.success("Artículo actualizado");
      } else {
        await axios.post(`${API}/articulos-catalogo`, formData, { withCredentials: true });
        toast.success("Artículo creado");
      }
      setShowDialog(false);
      setEditingItem(null);
      setFormData({ nombre: "", categoria: "LIM" });
      fetchArticulos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ nombre: item.nombre, categoria: item.categoria || "LIM" });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este artículo?")) return;
    try {
      await axios.delete(`${API}/articulos-catalogo/${id}`, { withCredentials: true });
      toast.success("Artículo eliminado");
      fetchArticulos();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/articulos-catalogo/seed`, {}, { withCredentials: true });
      if (res.data.seeded) {
        toast.success(res.data.message);
        fetchArticulos();
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error("Error al cargar datos iniciales");
    }
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setFormData({ nombre: "", categoria: "LIM" });
    setShowDialog(true);
  };

  const getClassInfo = (code) => CLASSIFICATIONS.find(c => c.value === code) || CLASSIFICATIONS[2];

  const filtered = articulos.filter(a => {
    const matchesSearch = a.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterClassification === "all" || a.categoria === filterClassification;
    return matchesSearch && matchesFilter;
  });

  const statsByClass = CLASSIFICATIONS.map(c => ({
    ...c,
    count: articulos.filter(a => a.categoria === c.value).length
  }));

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-articulos-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/configuracion/opciones")} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Catálogo de Artículos</h1>
              <p className="text-slate-500">Artículos disponibles para el módulo de Logística</p>
            </div>
          </div>
          <div className="flex gap-2">
            {articulos.length === 0 && (
              <Button onClick={handleSeed} variant="outline" data-testid="seed-articulos-button">
                Cargar Datos Iniciales
              </Button>
            )}
            <Button onClick={openNewDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-articulo-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Artículo
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Package className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{articulos.length}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {statsByClass.map(stat => (
            <Card key={stat.value}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${stat.color} bg-opacity-20 rounded-lg`}>
                    <stat.icon className={`w-5 h-5 ${stat.color.replace("bg-", "text-")}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.count}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar artículo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-articulos-input"
                  />
                </div>
              </div>
              <Select value={filterClassification} onValueChange={setFilterClassification}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Filtrar por clasificación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las clasificaciones</SelectItem>
                  {CLASSIFICATIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NOMBRE</TableHead>
                  <TableHead>CLASIFICACIÓN</TableHead>
                  <TableHead className="text-center">PRODUCTOS</TableHead>
                  <TableHead className="text-right">COSTO PROMEDIO</TableHead>
                  <TableHead className="text-right">TOTAL INVERTIDO</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">Cargando...</TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                      {articulos.length === 0 ? "No hay artículos. Usa 'Cargar Datos Iniciales' para comenzar." : "No hay resultados"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => {
                    const classInfo = getClassInfo(item.categoria);
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-semibold">{item.nombre}</TableCell>
                        <TableCell>
                          <Badge className={`${classInfo.color} text-white`}>
                            {classInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-medium">{item.productos_count || 0}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.costo_promedio > 0 ? (
                            <span className="font-semibold text-blue-700 flex items-center justify-end gap-1">
                              <DollarSign className="w-3 h-3" />
                              {item.costo_promedio.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">Sin datos</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.total_invertido > 0 ? (
                            <span className="font-semibold text-emerald-700">
                              ${item.total_invertido.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(item)} data-testid={`edit-articulo-${item.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)} data-testid={`delete-articulo-${item.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Editar Artículo" : "Nuevo Artículo"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Artículo *</Label>
                <Input
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Cloro, Papel Higiénico, Escoba..."
                  data-testid="articulo-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Clasificación *</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(v) => setFormData({ ...formData, categoria: v })}
                >
                  <SelectTrigger data-testid="articulo-categoria-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-articulo-button">
                  {editingItem ? "Guardar Cambios" : "Crear Artículo"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsArticulos;
